
import { Subject } from 'rxjs';
import { Injectable } from "@angular/core";
import { LocalStorageService } from 'ngx-webstorage';

import { AuthService } from './auth.service';
import { CryptographyService } from './cryptography.service';
import { LanguageService } from './language.service';

import { EventEmitter } from '../models/generic.class';

import CONFIGURATION from '../../configurations/configuration';
import { ENVIRONMENT } from '../../environments/environment';

@Injectable()
export class SsoService {
  userLoginServiceEventEmitter: Subject<any>;
  userOnLoginServiceEventEmitter: EventEmitter;
  FUSIONAUTH_SETTING = ENVIRONMENT.fusionAuth;
  constructor(
    private _authService: AuthService,
    private _cryptographyService: CryptographyService,
    private _languageService: LanguageService,
    private _localStorageService: LocalStorageService,
  ) {
    this.userLoginServiceEventEmitter = new Subject();
    this.userOnLoginServiceEventEmitter = new EventEmitter();
  }

  getFusionAuthClientId() {
    const countryCode = CONFIGURATION.country_code || String(localStorage.getItem('country'));
    return this.FUSIONAUTH_SETTING.clientId[countryCode.toLowerCase()];
  }

  getRedirectUri(): string {
    let pathName = '/admin-investor/overview';
    const params = new URLSearchParams(window.location.search);    /**
     *  To retain query params in the redirect URI
     */
    const faParams = [
      'code',
      'locale',
      'state',
      'userState'
    ];


    /**
     * Deleting all FA default query params
     */
    for (let i = 0; i < faParams.length; i++) {
      params.delete(faParams[i]);
    }

    const baseUrlPath = `${window.location.origin}${pathName}`;
    /**
     * Check if query params is exist
    */
    const redirectUri = Boolean(params.toString()) ? `?${params.toString()}` : '';
    return `${baseUrlPath}${redirectUri}`;
  }

  redirectToLoginPage() {
    const isIdLanguage = this._languageService.getDefaultLanguage() === 'id';
    const isThLanguage = this._languageService.getDefaultLanguage() === 'th';
    this._cryptographyService.generatePKCE().then(pkceChallenge => {
      const isBorrower = window.location.href.indexOf('/borrower') > -1;
      const state = this._cryptographyService.generateRandomString();
      this._localStorageService.store('state', state);
      this._localStorageService.store('codeVerifier', pkceChallenge.codeVerifier);
      const loginUrl = new URL('/oauth2/authorize', this.FUSIONAUTH_SETTING.baseUrl);
      const queryParams = loginUrl.searchParams;
      queryParams.set('client_id', this.getFusionAuthClientId());
      queryParams.set('response_type', 'code');
      queryParams.set('state', state);
      queryParams.set('scope', 'offline_access');
      queryParams.set('code_challenge', pkceChallenge.codeChallenge);
      queryParams.set('code_challenge_method', 'S256');
      if (isBorrower) {
        queryParams.set('is_borrower', 'true');
      }
      if (isIdLanguage || isThLanguage) {
        queryParams.set('locale', isIdLanguage ? 'id_ID' : 'th_TH');
      } else {
        queryParams.set('locale', 'en');
      }
      queryParams.set('redirect_uri', this.getRedirectUri());
      window.location.replace(loginUrl.href);
    });
  }

  async exchangeCodeForTokens(authCode: string) {
    const codeVerifier = this._localStorageService.retrieve('codeVerifier');

    const headers = new Headers();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');

    const urlencoded = new URLSearchParams();
    urlencoded.append('client_id', this.getFusionAuthClientId());
    urlencoded.append('code', authCode);
    urlencoded.append('grant_type', 'authorization_code');
    urlencoded.append('code_verifier', codeVerifier);
    urlencoded.append('redirect_uri', this.getRedirectUri());

    const tokenAPI = '/oauth2/token';
    const requestUrl = this.FUSIONAUTH_SETTING.baseUrl + tokenAPI;

    const accessTokenRequest = new Request(requestUrl, {
      method: 'POST',
      headers,
      body: urlencoded,
    });

    const apiResponse = await fetch(accessTokenRequest);
    if (apiResponse.ok) {
      const jsonResponse = await apiResponse.json();
      this.setAuthFromClaims(jsonResponse);
      return jsonResponse;
    }
    throw new Error('Error while exchanging code for tokens');
  }

  refreshJwtToken() {
    this._authService.setAccessTokenLastRefreshedAt(Date.now());
    const auth = JSON.parse(this._localStorageService.retrieve('auth'));
    if (auth) {
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      const requestUrl = `${this.FUSIONAUTH_SETTING.baseUrl}/api/jwt/refresh`;
      return fetch(requestUrl, {
        method: 'POST',
        body: JSON.stringify({
          refreshToken: auth?.refresh_token,
          token: auth?.access_token,
        }),
        headers,
      })
      .then(response => {
        if (response.ok) {
          return response.json(); // Return the promise from response.json()
        }
        throw new Error('failed to refresh token');
      })
      .then(jsonResponse => { // Handle the resolved value of response.json()
        const auth = JSON.parse(this._localStorageService.retrieve('auth'));
        auth.access_token = jsonResponse['token'];
        this._authService.setBearerToken( auth.access_token);
        return auth;
      });    }
  }

  redirectToLogoutPage() {
    this._localStorageService.clear('auth');
    this._localStorageService.clear('state');
    this._localStorageService.clear('accessTokenLastRefreshedAt');
    const redirectUri = new URL('/oauth2/logout', this.FUSIONAUTH_SETTING.baseUrl);
    redirectUri.searchParams.set('client_id', this.getFusionAuthClientId());
    window.location.replace(redirectUri.href);
  }

  setAuthFromClaims(tokenResponse: any) {
    const claims = this.parseJwt(tokenResponse.access_token);
    const auth: any = {
      access_token: tokenResponse.access_token,
      auth_user_uuid: claims.auth_user_uuid,
      member_uuid: claims.member_uuid,
      scfs_user_uuid: claims.scfs_user_uuid,
      token_type: tokenResponse.token_type,
      refresh_token: tokenResponse.refresh_token,
      expires_in: tokenResponse.expires_in,
      scope: tokenResponse.scope,
      member_id: claims.member_id,
      intercom_hash: null,
      next_step: 'dashboard',
      default_role: claims.default_role,
      first_name: claims.first_name,
    };
    this._localStorageService.store('auth', JSON.stringify(auth));
    this._localStorageService.store('accessTokenLastRefreshedAt', String(Date.now()));
  }

  parseJwt(token: string) {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    return JSON.parse(jsonPayload);
  }


}
